import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import transactionService from '../../services/transactionService';
import currencyService from '../../services/currencyService';
import { Button, Input, Select, Table, Card, Pagination, ConfirmDialog } from '../common';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FunnelIcon, ArrowPathIcon, XCircleIcon, TrashIcon, EyeIcon, DocumentDuplicateIcon, ArrowDownTrayIcon, ArrowPathRoundedSquareIcon } from '@heroicons/react/24/outline';
import ReceiptActions from './ReceiptActions';

const TransactionList = ({ onRefresh, onRepeatTransaction, initialFilters }) => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [onlyDeleted, setOnlyDeleted] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: initialFilters?.dateFrom || '',
    dateTo: initialFilters?.dateTo || '',
    currencyCode: initialFilters?.currencyCode || '',
    customerName: initialFilters?.customerName || '',
    customerUuid: initialFilters?.customerUuid || '',
    status: initialFilters?.status || ''
  });

  // Cancel/Delete dialog states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchCurrencies();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [page, limit, onRefresh, onlyDeleted]);

  const fetchCurrencies = async () => {
    try {
      const response = await currencyService.getCurrencies(true);
      setCurrencies(response.success ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch currencies:', error);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        onlyDeleted,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '' && value !== 'all')
        )
      };
      const response = await transactionService.getTransactions(params);
      if (response.success) {
        setTransactions(response.data.transactions || response.data);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (error) {
      toast.error(t('common.error'));
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchTransactions();
  };

  const handleResetFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      currencyCode: '',
      customerName: '',
      customerUuid: '',
      status: ''
    });
    setPage(1);
    fetchTransactions();
  };

  // Cancel transaction handlers
  const handleCancelClick = (transaction) => {
    setSelectedTransaction(transaction);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async (reason) => {
    if (!selectedTransaction) return;

    setActionLoading(true);
    try {
      const response = await transactionService.cancelTransaction(selectedTransaction.uuid, reason);
      if (response.success) {
        toast.success(t('transactions.transactionCancelled'));
        fetchTransactions();
      } else {
        toast.error(response.message || t('common.error'));
      }
    } catch (error) {
      toast.error(t('common.error'));
      console.error('Failed to cancel transaction:', error);
    } finally {
      setActionLoading(false);
      setCancelDialogOpen(false);
      setSelectedTransaction(null);
    }
  };

  // Delete transaction handlers
  const handleDeleteClick = (transaction) => {
    setSelectedTransaction(transaction);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTransaction) return;

    setActionLoading(true);
    try {
      const response = await transactionService.deleteTransaction(selectedTransaction.uuid);
      if (response.success) {
        toast.success(t('transactions.transactionDeleted'));
        fetchTransactions();
      } else {
        toast.error(response.message || t('common.error'));
      }
    } catch (error) {
      toast.error(t('common.error'));
      console.error('Failed to delete transaction:', error);
    } finally {
      setActionLoading(false);
      setDeleteDialogOpen(false);
      setSelectedTransaction(null);
    }
  };

  const handleRestore = async (uuid) => {
    if (!window.confirm(t('transactions.confirmRestore') || 'Are you sure you want to restore this transaction?')) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await transactionService.restoreTransaction(uuid);
      if (response.success) {
        toast.success(response.message || 'Transaction restored');
        fetchTransactions();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  // Export transactions to CSV/Excel
  const handleExport = async () => {
    setExporting(true);
    try {
      // Fetch all filtered transactions for export (up to 10000)
      const params = {
        page: 1,
        limit: 10000,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      };
      const response = await transactionService.getTransactions(params);
      const exportData = response.success ? (response.data.transactions || response.data) : [];

      if (exportData.length === 0) {
        toast.error(t('transactions.noDataToExport'));
        return;
      }

      // Create CSV content with headers
      // Create CSV content with headers
      const headers = [
        t('transactions.date'),
        t('transactions.customerName'),
        t('transactions.customerPhone'),
        t('transactions.currencyIn'),
        t('transactions.amountIn'),
        t('transactions.currencyOut'),
        t('transactions.amountOut'),
        t('transactions.exchangeRate'),
        t('transactions.profit'),
        t('transactions.employee'),
        t('common.status')
      ].join(',');

      const rows = exportData.map(tx => [
        formatDate(tx.transactionDate),
        `"${(tx.customerName || '').replace(/"/g, '""')}"`,
        `"${(tx.customerPhone || '').replace(/"/g, '""')}"`,
        tx.currencyIn?.code || '',
        formatCurrency(tx.amountIn),
        tx.currencyOut?.code || '',
        formatCurrency(tx.amountOut),
        formatCurrency(tx.exchangeRate),
        formatCurrency(tx.profit || 0),
        `"${(tx.employee?.fullName || '').replace(/"/g, '""')}"`,
        tx.status || 'completed'
      ].join(','));

      const csvContent = [headers, ...rows].join('\n');

      // Add BOM for Excel UTF-8 compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `transactions-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(t('transactions.exportSuccess'));
    } catch (error) {
      toast.error(t('transactions.exportError'));
      console.error('Failed to export transactions:', error);
    } finally {
      setExporting(false);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusStyles = {
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };

    const statusLabels = {
      completed: t('transactions.completed'),
      cancelled: t('transactions.cancelled')
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  // Status filter options
  const statusOptions = [
    { value: '', label: t('common.all') },
    { value: 'completed', label: t('transactions.completed') },
    { value: 'cancelled', label: t('transactions.cancelled') }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const currencyOptions = currencies.map(c => ({
    value: c.code,
    label: `${c.code} - ${c.name}`
  }));

  const columns = [
    {
      header: t('transactions.date'),
      accessor: 'transactionDate',
      render: (value, row) => (
        <span className={`text-gray-600 text-sm ${row.status === 'cancelled' || row.deletedAt ? 'line-through' : ''}`}>
          {formatDate(value)}
        </span>
      )
    },
    {
      header: t('transactions.customerName'),
      accessor: 'customerName',
      render: (value, row) => (
        <div className={row.status === 'cancelled' || row.deletedAt ? 'line-through opacity-60' : ''}>
          <div className="font-medium text-gray-900">{value || '-'}</div>
          {row.customerPhone && (
            <div className="text-sm text-gray-500">{row.customerPhone}</div>
          )}
        </div>
      )
    },
    {
      header: t('transactions.currencyIn'),
      accessor: 'currencyIn',
      render: (_, row) => (
        <span className={`text-green-600 font-medium ${row.status === 'cancelled' || row.deletedAt ? 'line-through opacity-60' : ''}`}>
          {row.currencyIn?.code} {formatCurrency(row.amountIn)}
        </span>
      )
    },
    {
      header: t('transactions.currencyOut'),
      accessor: 'currencyOut',
      render: (_, row) => (
        <span className={`text-red-600 font-medium ${row.status === 'cancelled' || row.deletedAt ? 'line-through opacity-60' : ''}`}>
          {row.currencyOut?.code} {formatCurrency(row.amountOut)}
        </span>
      )
    },
    {
      header: t('transactions.exchangeRate'),
      accessor: 'exchangeRate',
      render: (value, row) => (
        <span className={`text-gray-700 ${row.status === 'cancelled' || row.deletedAt ? 'line-through opacity-60' : ''}`}>
          {formatCurrency(value)}
        </span>
      )
    },
    {
      header: onlyDeleted ? (t('audit.actions.delete') + ' ' + t('common.by')) : t('common.status'),
      accessor: onlyDeleted ? 'deletedByName' : 'status',
      render: (value, row) => (
        onlyDeleted ? (
          <div className="text-sm">
            <div className="font-medium text-red-600">{value || '-'}</div>
            <div className="text-xs text-gray-500">{formatDate(row.deletedAt)}</div>
          </div>
        ) : (
          <StatusBadge status={value || 'completed'} />
        )
      )
    },
    {
      header: t('transactions.employee'),
      accessor: 'employee',
      render: (_, row) => (
        <span className={`text-gray-500 ${row.status === 'cancelled' || row.deletedAt ? 'line-through opacity-60' : ''}`}>
          {row.employee?.fullName || '-'}
        </span>
      )
    },
    {
      header: onlyDeleted ? '' : (t('receipts.print') || 'Receipt'),
      accessor: 'receipt',
      render: (_, row) => (
        onlyDeleted ? null : <ReceiptActions transaction={row} size="sm" />
      )
    },
    {
      header: t('common.actions'),
      accessor: 'actions',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          {onlyDeleted ? (
            <button
              onClick={() => handleRestore(row.uuid)}
              className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded flex items-center gap-1"
              title={t('transactions.repeatTransaction')}
            >
              <ArrowPathRoundedSquareIcon className="h-5 w-5" />
              <span className="text-xs font-medium">{t('common.restore') || 'Restore'}</span>
            </button>
          ) : (
            <>
              <Link
                to={`/transactions/${row.uuid}`}
                className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                title={t('transactions.viewDetails')}
              >
                <EyeIcon className="h-5 w-5" />
              </Link>
              {row.status !== 'cancelled' && onRepeatTransaction && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRepeatTransaction({
                      customerName: row.customerName,
                      customerPhone: row.customerPhone,
                      currencyInId: row.currencyInId,
                      currencyOutId: row.currencyOutId,
                      amountIn: row.amountIn,
                      exchangeRate: row.exchangeRate,
                      amountOut: row.amountOut
                    });
                  }}
                  className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                  title={t('transactions.repeatTransaction')}
                >
                  <DocumentDuplicateIcon className="h-5 w-5" />
                </button>
              )}
              {row.status !== 'cancelled' && (
                <button
                  onClick={() => handleCancelClick(row)}
                  className="p-1 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded"
                  title={t('transactions.cancelTransaction')}
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
              {isAdmin() && (
                <button
                  onClick={() => handleDeleteClick(row)}
                  className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                  title={t('transactions.deleteTransaction')}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              )}
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-4">
      {/* Action Buttons */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FunnelIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
            {t('common.filter')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={fetchTransactions}
          >
            <ArrowPathIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
            {t('common.refresh')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExport}
            disabled={exporting || transactions.length === 0}
          >
            <ArrowDownTrayIcon className={`h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2 ${exporting ? 'animate-bounce' : ''}`} />
            {exporting ? t('common.loading') : t('transactions.exportToExcel')}
          </Button>
        </div>

        {isAdmin() && (
          <button
            onClick={() => {
              setOnlyDeleted(!onlyDeleted);
              setPage(1);
            }}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              onlyDeleted 
                ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <TrashIcon className="h-4 w-4" />
            {onlyDeleted ? (t('common.viewAll') || 'View Active') : (t('common.recycleBin') || 'Recycle Bin')}
          </button>
        )}
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="bg-gray-50">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Input
              label={t('transactions.date') + ' (' + t('common.from') + ')'}
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
            <Input
              label={t('transactions.date') + ' (' + t('common.to') + ')'}
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
            <Select
              label={t('currencies.code')}
              options={currencyOptions}
              placeholder={t('common.all')}
              value={filters.currencyCode}
              onChange={(e) => handleFilterChange('currencyCode', e.target.value)}
            />
            <Input
              label={t('transactions.customerName')}
              placeholder={t('common.search')}
              value={filters.customerName}
              onChange={(e) => handleFilterChange('customerName', e.target.value)}
            />
            <Select
              label={t('common.status')}
              options={statusOptions}
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 mt-4">
            <Button size="sm" onClick={handleApplyFilters}>
              {t('common.filter')}
            </Button>
            <Button variant="secondary" size="sm" onClick={handleResetFilters}>
              {t('common.cancel')}
            </Button>
          </div>
        </Card>
      )}

      {/* Active Customer Filter Alert */}
      {filters.customerUuid && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-blue-700 text-sm">
            <FunnelIcon className="h-4 w-4" />
            <span>{t('transactions.filteredByCustomer') || 'Viewing transactions for a specific customer'}</span>
          </div>
          <button
            onClick={handleResetFilters}
            className="text-blue-700 hover:text-blue-900 text-sm font-medium"
          >
            {t('common.clearFilter') || 'Clear Filter'}
          </button>
        </div>
      )}

      {/* Transactions Table */}
      <Table
        columns={columns}
        data={transactions}
        loading={loading}
        emptyMessage={t('common.noData')}
        onRowClick={(row) => navigate(`/transactions/${row.uuid}`)}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{t('common.rowsPerPage') || 'Rows per page'}:</span>
            <select
              value={limit}
              onChange={handleLimitChange}
              className="border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="500">500</option>
            </select>
          </div>
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* Cancel Transaction Dialog */}
      <ConfirmDialog
        isOpen={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          setSelectedTransaction(null);
        }}
        onConfirm={handleCancelConfirm}
        title={t('transactions.cancelTransaction')}
        message={t('transactions.confirmCancel')}
        confirmText={t('transactions.cancelTransaction')}
        confirmVariant="danger"
        showReasonInput={true}
        reasonLabel={t('transactions.cancellationReason')}
        reasonPlaceholder={t('transactions.enterReason')}
        reasonRequired={false}
        loading={actionLoading}
      />

      {/* Delete Transaction Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedTransaction(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('transactions.deleteTransaction')}
        message={t('transactions.confirmDelete')}
        confirmText={t('common.delete')}
        confirmVariant="danger"
        showReasonInput={false}
        loading={actionLoading}
      />

    </div>
  );
};

export default TransactionList;